import React, {useContext, useEffect, useState, useRef} from 'react';
import {AppContext} from '../context/AppContext';
import {Worklog} from '../services/Worklog';
import WorklogList from "./WorklogList";
import IssueAutocompleteInput from "./IssueAutocompleteInput";
import DescriptionAutocompleteInput from "./DescriptionAutocompleteInput";
import FavoritesCard from "./FavoritesCard";
import {Favorite} from "../types/storage";
import {DescriptionHistoryService} from '../services/DescriptionHistoryService';

const TimerView: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('TimerView must be used within AppContextProvider');
  }

  const {timer, i18n, startTimer, stopTimer, autoSync, store, syncQueue} = context;

  const [timerIssue, setTimerIssue] = useState('');
  const [timerDesc, setTimerDesc] = useState('');
  const [isEditingIssue, setIsEditingIssue] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [startDateTime, setStartDateTime] = useState('');
  const [isEditingStart, setIsEditingStart] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [showManualForm, setShowManualForm] = useState(false);

  // Manual form state
  const [manualIssue, setManualIssue] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [weeklyTotalSec, setWeeklyTotalSec] = useState(() => store.weeklyTotalSec());

  // Track previous running state to detect transition and clear fields once
  const prevRunningRef = useRef<boolean>(timer.isRunning());

  // Helpers for datetime-local format (YYYY-MM-DDTHH:mm)
  const formatDateTimeLocal = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const parseDateTimeLocal = (s: string): Date | null => {
    if (!s || !s.trim()) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  // Update elapsed time and sync with context timer
  useEffect(() => {
    const updateElapsed = () => {
      const isTimerRunning = timer.isRunning();

      if (isTimerRunning) {
        const active = timer.active();
        if (active && active.start) {
          const elapsedSeconds = timer.elapsedSec();
          const hours = Math.floor(elapsedSeconds / 3600);
          const minutes = Math.floor((elapsedSeconds % 3600) / 60);
          const seconds = elapsedSeconds % 60;
          setElapsed(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);

          // Update local state to match timer unless user is editing
          if (!isEditingIssue && active.issueKey !== timerIssue) setTimerIssue(active.issueKey);
          if (!isEditingDesc && active.description !== timerDesc) setTimerDesc(active.description);
          const formattedStart = formatDateTimeLocal(active.start);
          if (!isEditingStart && formattedStart !== startDateTime) setStartDateTime(formattedStart);
        }
      } else {
        setElapsed('00:00:00');
        // Clear fields once when transitioning from running -> stopped
        if (prevRunningRef.current) {
          if (!isEditingIssue) setTimerIssue('');
          if (!isEditingDesc) setTimerDesc('');
          if (!isEditingStart) setStartDateTime('');
        }
      }

      // Update previous running state after handling
      prevRunningRef.current = isTimerRunning;
    };

    const interval = setInterval(updateElapsed, 1000);
    updateElapsed(); // Initial call

    return () => clearInterval(interval);
  }, [timer, timerIssue, timerDesc, isEditingIssue, isEditingDesc, isEditingStart, startDateTime]);

  // Update weekly total when store changes
  useEffect(() => {
    const updateWeeklyTotal = () => {
      // Reload from storage to reflect changes made by SyncQueue or other tabs
      if (store && typeof store.reload === 'function') {
        store.reload();
      }
      setWeeklyTotalSec(store.weeklyTotalSec());
    };

    // Update immediately
    updateWeeklyTotal();

    // Set up interval to check for changes (simple polling approach)
    const interval = setInterval(updateWeeklyTotal, 1000);

    return () => clearInterval(interval);
  }, [store]);

  const isRunning = timer.isRunning();

  const handleStartStop = () => {
    if (!isRunning) {
      // Start timer using provided Start Date/Time or now
      const specified = parseDateTimeLocal(startDateTime);
      const when = specified ?? new Date();
      startTimer(timerIssue.trim(), timerDesc.trim(), when);
    } else {
      // Stop timer
      const worklog = stopTimer();
      if (worklog) {
        // Add description to history
        if (worklog.description) {
          DescriptionHistoryService.addDescription(worklog.description);
        }
        const w = new Worklog(worklog);
        store.add(w);
        syncQueue.enqueueIfAuto(w);
        // Clear form after stopping
        setTimerIssue('');
        setTimerDesc('');
        setStartDateTime('');
      }
    }
  };

  const handleManualEntrySave = () => {
    if (manualIssue.trim() && manualDesc.trim() && manualDate && manualStart && manualEnd) {
      // Parse date and time
      const startDateTime = new Date(`${manualDate}T${manualStart}`);
      const endDateTime = new Date(`${manualDate}T${manualEnd}`);

      // this.clearManualErrors();
      const w = new Worklog({
        issueKey: manualIssue.trim(),
        description: manualDesc.trim(),
        start: startDateTime,
        end: endDateTime,
        status: autoSync ? 'queued' : 'local'
      });
      const errors = w.validate(i18n);
      if (Object.keys(errors).length) {
        console.log('Manual worklog validation errors:', errors);
        // TODO show errors
        // this.showManualErrors(errors);
        return;
      }

      // Add description to history
      if (w.description) {
        DescriptionHistoryService.addDescription(w.description);
      }

      store.add(w);
      syncQueue.enqueueIfAuto(w);

      setShowManualForm(false);
      // Reset manual form
      setManualIssue('');
      setManualDesc('');
      setManualDate('');
      setManualStart('');
      setManualEnd('');

    }
  };

  const handleManualEntryCancel = () => {
    setShowManualForm(false);
    // Reset manual form
    setManualIssue('');
    setManualDesc('');
    setManualDate('');
    setManualStart('');
    setManualEnd('');
  };

  const handleUseFavorite = (fav: Favorite) => {
    // Stop current timer if running and persist worklog
    if (timer.isRunning()) {
      const worklog = stopTimer();
      if (worklog) {
        // Add stopped worklog description to history
        if (worklog.description) {
          DescriptionHistoryService.addDescription(worklog.description);
        }
        store.add(new Worklog(worklog));
      }
    }
    const key = fav.issueKey;
    const desc = fav.description || '';
    setTimerIssue(key);
    setTimerDesc(desc);
    startTimer(key, desc);
  };

  return (
    <section id="timer-view" className="view">
      <FavoritesCard onUseFavorite={handleUseFavorite} />
      <div className="card timer-card">
        <div className="timer-form">
          <div className="field">
            <label>
              <span>{i18n.t('issueKey')}</span>
              <IssueAutocompleteInput
                inputId="timer-issue"
                value={timerIssue}
                onChange={(v) => setTimerIssue(v)}
                onFocus={() => setIsEditingIssue(true)}
                onBlur={() => {
                  setIsEditingIssue(false);
                  if (timer.isRunning()) {
                    timer.setIssueKey(timerIssue.trim());
                  }
                }}
                placeholder="PROJ-123"
                required
                pattern="[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*"
                autoComplete="off"
              />
            </label>
          </div>
          <div className="field">
            <label>
              <span>{i18n.t('description')}</span>
              <DescriptionAutocompleteInput
                inputId="timer-desc"
                value={timerDesc}
                onChange={setTimerDesc}
                placeholder={i18n.t('descPlaceholder')}
                onFocus={() => setIsEditingDesc(true)}
                onBlur={() => {
                  setIsEditingDesc(false);
                  if (timer.isRunning()) {
                    timer.setDescription(timerDesc.trim());
                  }
                }}
              />
            </label>
          </div>
            <div className="field">
              <label>
                <span>{i18n.t('startDateTime')}</span>
                <input
                  id="timer-start"
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  onFocus={() => setIsEditingStart(true)}
                  onBlur={(e) => {
                    setIsEditingStart(false);
                    const d = parseDateTimeLocal(e.target.value);
                    if (d && timer.isRunning()) {
                      timer.adjustStart(d);
                    }
                  }}
                />
              </label>
            </div>
          <div className="timer-actions">
            <div className="elapsed" id="elapsed">{elapsed}</div>
            <button
              id="start-stop-btn"
              className={`btn ${isRunning ? 'danger' : 'success'}`}
              onClick={handleStartStop}
            >
              {isRunning ? i18n.t('stop') : i18n.t('start')}
            </button>
          </div>
        </div>
      </div>

      <div className="card manual-entry">
        <button
          id="toggle-manual-btn"
          className="btn"
          onClick={() => {
            const opening = !showManualForm;
            setShowManualForm(opening);
            if (opening) {
              const d = new Date();
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              setManualDate(`${yyyy}-${mm}-${dd}`);
              setManualStart('08:00');
              setManualEnd('08:15');
            }
          }}
        >
          {i18n.t('addManualEntry')}
        </button>
        <form id="manual-form" className={showManualForm ? '' : 'hidden'}>
          <div className="row">
            <div className="field">
              <label>
                <span>{i18n.t('issueKey')}</span>
                <IssueAutocompleteInput
                  inputId="manual-issue"
                  value={manualIssue}
                  onChange={setManualIssue}
                  placeholder="PROJ-123"
                  required
                  pattern="[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*"
                  autoComplete="off"
                />
              </label>
              <div className="error" id="manual-issue-error"></div>
            </div>
            <div className="field">
              <label>
                <span>{i18n.t('description')}</span>
                <DescriptionAutocompleteInput
                  inputId="manual-desc"
                  value={manualDesc}
                  onChange={setManualDesc}
                  placeholder={i18n.t('description')}
                />
              </label>
              <div className="error" id="manual-desc-error"></div>
            </div>
            <div className="field">
              <label>
                <span>{i18n.t('date')}</span>
                <input
                    id="manual-date"
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                />
              </label>
              <div className="error" id="manual-date-error"></div>
            </div>
            <div className="field">
              <label>
                <span>{i18n.t('startTime')}</span>
                <input
                    id="manual-start"
                    type="time"
                    value={manualStart}
                    onChange={(e) => setManualStart(e.target.value)}
                />
              </label>
              <div className="error" id="manual-start-error"></div>
            </div>
            <div className="field">
              <label>
                <span>{i18n.t('endTime')}</span>
                <input
                    id="manual-end"
                    type="time"
                    value={manualEnd}
                    onChange={(e) => setManualEnd(e.target.value)}
                />
              </label>
              <div className="error" id="manual-end-error"></div>
            </div>
          </div>
          <div className="row right">
            <button
              id="manual-cancel"
              type="button"
              className="btn"
              onClick={handleManualEntryCancel}
            >
              {i18n.t('cancel')}
            </button>
            <button
              id="manual-save"
              type="button"
              className="btn primary"
              onClick={handleManualEntrySave}
            >
              {i18n.t('save')}
            </button>
          </div>
        </form>
      </div>

      <div className="card totals">
        <div id="weekly-total">
          <h3>{i18n.t('weeklyTotal')}</h3>
          <div className="total-time">
            {(() => {
              const hours = Math.floor(weeklyTotalSec / 3600);
              const minutes = Math.floor((weeklyTotalSec % 3600) / 60);
              return `${hours}h ${minutes}m`;
            })()}
          </div>
        </div>
      </div>

      <WorklogList/>
    </section>
  );
};

export default TimerView;